import { useEffect, useRef, useState } from 'react'
import { Plus } from 'lucide-react'
import { DrawerSection } from '@/shared/ui/Drawer'
import { ErrorStrip, Note } from '@/shared/ui/states'
import { useCan } from '@/shared/auth/session'
import { explain } from '@/shared/api/problem'
import { fmtDate } from '@/shared/lib/format'
import type { VoterTagResponse } from '@/shared/api/types'
import { addTag, removeTag, voterTags } from './api'
import { TagChip, TagPicker, useTagVocabulary } from './tags'

/**
 * A voter's tags, in the drawer.
 *
 * ## This is the screen the whole feature was missing
 *
 * The backend has had `GET/POST/DELETE /voters/{id}/tags` since Phase 3. The web
 * client has had the three API functions since then too — and called none of
 * them. So the targeting presets that depend on tags had no way for anybody to
 * put a tag on anybody, and the programme rule builder reported "nobody tagged
 * yet" about a register nobody could tag.
 *
 * ## Adding is `voter:update`, not `voter:tag`
 *
 * A mobilizer editing one person's record already holds `voter:update`, and
 * demanding a second authority to tick a box on a form they can otherwise edit
 * would be theatre. `voter:tag` gates the vocabulary and the bulk path — the
 * things that act on many voters at once.
 *
 * Creating a NEW tag from here is possible (the server registers an unknown tag
 * on first use) but is offered only to holders of `voter:tag`. One person
 * inventing a spelling in a drawer is exactly how a vocabulary rots, and the
 * people who own the vocabulary are the people who should be able to grow it.
 */
export function VoterTagsSection({ voterId }: { voterId: string }) {
  const canUpdate = useCan('voter:update')
  const canManage = useCan('voter:tag')

  const [rows, setRows] = useState<VoterTagResponse[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)

  const vocab = useTagVocabulary({ activeOnly: true })

  useEffect(() => {
    let alive = true
    setRows(null)
    voterTags(voterId)
      .then((r) => { if (alive) { setRows(r); setError(null) } })
      .catch((e) => { if (alive) { setRows([]); setError(explain(e)) } })
    return () => { alive = false }
  }, [voterId])

  // The one async path in this file that had no guard. Closing the drawer mid-drop
  // wrote state into a component that was gone; every other path here uses `alive`.
  const alive = useRef(true)
  useEffect(() => {
    alive.current = true
    return () => { alive.current = false }
  }, [])

  const reload = async () => {
    try {
      const next = await voterTags(voterId)
      if (alive.current) { setRows(next); setError(null) }
    } catch (e) { if (alive.current) setError(explain(e)) }
  }

  const apply = async (raw: string) => {
    setBusy(raw); setError(null)
    try {
      await addTag(voterId, raw)
      await reload()
      if (!alive.current) return
      vocab.reload()          // a newly-invented tag has to appear in the picker
      setAdding(false)
    } catch (e) { if (alive.current) setError(explain(e)) }
    finally { if (alive.current) setBusy(null) }
  }

  const drop = async (tag: string) => {
    setBusy(tag); setError(null)
    try {
      await removeTag(voterId, tag)
      await reload()
    } catch (e) { if (alive.current) setError(explain(e)) }
    finally { if (alive.current) setBusy(null) }
  }

  const applied = rows?.map((r) => r.tag) ?? []

  return (
    <DrawerSection
      title="Tags"
      right={canUpdate && (
        <button type="button" className="erp-btn h-7 text-erp-xs"
          onClick={() => setAdding((a) => !a)} aria-expanded={adding}>
          <Plus className="h-3.5 w-3.5" /> {adding ? 'Done' : 'Add'}
        </button>
      )}
    >
      <div className="px-4 py-3 space-y-3">
        <ErrorStrip message={error ?? vocab.error} />

        {rows === null ? (
          <p className="text-erp-xs text-muted-foreground">Loading tags…</p>
        ) : rows.length === 0 ? (
          <p className="text-erp-xs text-muted-foreground">
            No tags. Tags are what the occupation-based targeting presets match on — there is
            no occupation column on a voter record, so <span className="font-medium">boda
            rider</span>, <span className="font-medium">mama mboga</span> and the rest are
            tags or they are nothing.
          </p>
        ) : (
          <ul className="flex flex-wrap gap-1.5">
            {rows.map((r) => (
              <li key={r.tag}>
                <TagChip
                  tag={r.tag}
                  displayName={r.displayName}
                  onRemove={canUpdate && busy !== r.tag ? () => void drop(r.tag) : undefined}
                />
                <span className="sr-only">
                  applied {fmtDate(r.appliedAt)}{r.source ? ` from ${r.source}` : ''}
                </span>
              </li>
            ))}
          </ul>
        )}

        {adding && canUpdate && vocab.tags === null && !vocab.error && (
          <p className="text-erp-xs text-muted-foreground">Loading the tag vocabulary…</p>
        )}
        {adding && canUpdate && vocab.tags === null && vocab.error && (
          <p className="text-erp-xs text-red-700 dark:text-red-300">
            The tag vocabulary could not be loaded, so there is nothing to pick from. That is
            not the same as there being no tags.
          </p>
        )}
        {adding && canUpdate && vocab.tags && (
          <div className="rounded-md border border-border p-2.5">
            <TagPicker
              vocabulary={vocab.tags}
              exclude={applied}
              autoFocus
              onPick={(t) => void apply(t.tag)}
              // Only a vocabulary owner may invent one from here.
              onCreate={canManage ? (raw) => void apply(raw) : undefined}
              placeholder="Search tags, or type a new one"
            />
            {!canManage && (
              <p className="mt-2 text-erp-xs text-muted-foreground">
                You can apply an existing tag. Creating a new one needs the tag-management
                authority — which is deliberate: a vocabulary anybody can extend stops being
                a vocabulary.
              </p>
            )}
            {busy && <p className="mt-2 text-erp-xs text-muted-foreground">Saving…</p>}
          </div>
        )}

        {rows !== null && rows.length > 0 && (
          <Note tone="info">
            A tag is matched EXACTLY by targeting rules. The chip shows the label; hover it to
            see the stored form, which is what a preset compares against.
          </Note>
        )}
      </div>
    </DrawerSection>
  )
}
